import { useState } from "react";
import UserAutocomplete from "admin/components/user-management/UserAutocomplete";
import useGroups from "admin/hooks/useGroups";
import useUsers from "admin/hooks/useUsers";
import {
  GrantItem,
  GrantListResponse,
  GrantPermission,
  PendingGrantAddition,
  PendingGrantRemoval,
  PendingGrantUpdate,
} from "admin/types/interfaces/grant";
import { findUserByEmail, getUserNameByEmail, userPrincipal } from "admin/utils/users";
import { AlertTriangle, ChevronRight, HelpCircle, Lock, Shield, Users } from "lucide-react";
import { PersonAvatar } from "~/components/PersonAvatar";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";

const PERMISSION_ORDER: GrantPermission[] = [
  GrantPermission.can_manage,
  GrantPermission.can_edit,
  GrantPermission.can_view,
];

export const PERMISSION_LABELS: Record<GrantPermission, string> = {
  can_manage: "Manage",
  can_edit: "Edit",
  can_view: "View",
};

const HelpTooltip = ({ content }: { content: React.ReactNode }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
    </TooltipTrigger>
    <TooltipContent className="max-w-64">{content}</TooltipContent>
  </Tooltip>
);

interface AdminGrantsPanelProps {
  grantsUrl: string;
  // The entity the drawer is administering — used to phrase the access tooltips.
  resourceType: "user" | "group";
  pendingAdditions: PendingGrantAddition[];
  pendingRemovals: PendingGrantRemoval[];
  pendingUpdates: PendingGrantUpdate[];
  onAddAccess: (addition: PendingGrantAddition) => void;
  onRemoveAccess: (removal: PendingGrantRemoval) => void;
  onUpdateAccess: (update: PendingGrantUpdate) => void;
  canEdit: boolean;
}

function principalTypeAndId(principal: string): {
  type: "user" | "group";
  id: string;
} {
  const colonIdx = principal.indexOf(":");
  if (colonIdx === -1) return { type: "user", id: principal };
  const prefix = principal.slice(0, colonIdx);
  const id = principal.slice(colonIdx + 1);
  return { type: prefix === "user-tag" ? "group" : "user", id };
}

function getEffectivePermission(
  item: GrantItem,
  pendingUpdates: PendingGrantUpdate[]
): GrantPermission {
  const update = pendingUpdates.find(
    (u) => u.principal === item.principal && u.oldPermission === item.permission
  );
  return update?.newPermission ?? item.permission;
}

const AdminGrantsPanel = (props: AdminGrantsPanelProps) => (
  <>
    <Alert variant="warning" className="mt-6">
      <AlertTriangle />
      <AlertTitle>Support overrides only</AlertTitle>
      <AlertDescription>
        <p>
          This tab edits OpenFGA grants directly, controlling{" "}
          <strong>who can view, edit, or manage</strong> this {props.resourceType}. Use it only for
          support troubleshooting. Day-to-day access should come from roles and group membership.
        </p>
      </AlertDescription>
    </Alert>
    <DirectGrants {...props} />
  </>
);

const DirectGrants = ({
  grantsUrl,
  resourceType,
  pendingAdditions,
  pendingRemovals,
  pendingUpdates,
  onAddAccess,
  onRemoveAccess,
  onUpdateAccess,
  canEdit,
}: AdminGrantsPanelProps) => {
  const resourceNoun = resourceType === "group" ? "group" : "user";
  const { data, isLoading, error } = useFetcher<GrantListResponse>(grantsUrl);
  const { data: groups = [] } = useGroups();
  const { data: users = [] } = useUsers();
  const [selectedGroup, setSelectedGroup] = useState<string>("");

  const allGrants = data?.items ?? [];
  const directGrants = allGrants.filter((g) => g.source === "direct");

  const removedKeys = new Set(pendingRemovals.map((r) => `${r.principal}:${r.permission}`));

  const pendingUserAdditions = pendingAdditions.filter((a) => a.type === "user");
  const pendingGroupAdditions = pendingAdditions.filter((a) => a.type === "group");

  const existingUserPrincipals = new Set([
    ...directGrants
      .filter((g) => principalTypeAndId(g.principal).type === "user")
      .map((g) => userPrincipal(principalTypeAndId(g.principal).id)),
    ...pendingUserAdditions.map((a) => userPrincipal(a.identifier)),
  ]);
  const existingGroupPrincipals = new Set([
    ...directGrants
      .filter((g) => principalTypeAndId(g.principal).type === "group")
      .map((g) => g.principal),
    ...pendingGroupAdditions.map((a) => `user-tag:${a.identifier}`),
  ]);

  const visibleUserGrants = directGrants.filter(
    (g) => principalTypeAndId(g.principal).type === "user"
  );
  const visibleGroupGrants = directGrants.filter(
    (g) => principalTypeAndId(g.principal).type === "group"
  );

  const availableGroups = groups.filter(
    (g) => !g.is_internal && !existingGroupPrincipals.has(`user-tag:${g.name}`)
  );

  const handleAddUser = (user: { email: string; full_name: string }) => {
    const principal = userPrincipal(user.email);
    if (existingUserPrincipals.has(principal)) return;
    onAddAccess({
      type: "user",
      identifier: user.email,
      label: user.full_name || user.email,
      permission: GrantPermission.can_view,
    });
  };

  const handleAddGroup = (name: string) => {
    const principal = `user-tag:${name}`;
    if (existingGroupPrincipals.has(principal)) return;
    const group = groups.find((g) => g.name === name);
    onAddAccess({
      type: "group",
      identifier: name,
      label: group?.display_name || name,
      permission: GrantPermission.can_view,
    });
    setSelectedGroup("");
  };

  if (isLoading) {
    return (
      <div className="space-y-2 mt-6">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div key={idx} className="h-10 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="mt-6 p-2 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm">
        Unable to load access information
      </div>
    );
  }

  const totalUsers = visibleUserGrants.length + pendingUserAdditions.length;
  const totalGroups = visibleGroupGrants.length + pendingGroupAdditions.length;

  if (totalUsers === 0 && totalGroups === 0 && !canEdit) {
    return (
      <div className="mt-6 flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No Access Grants</h3>
        <p className="text-sm text-muted-foreground">
          No direct grants have been assigned on this resource.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <Label>Users ({totalUsers})</Label>
          <HelpTooltip
            content={
              <div className="space-y-1.5">
                <p>Users with access to this {resourceNoun}, by access level:</p>
                <ul className="space-y-1">
                  <li>
                    <span className="font-medium">View:</span> read-only.
                  </li>
                  <li>
                    <span className="font-medium">Edit:</span> membership changes.
                  </li>
                  <li>
                    <span className="font-medium">Manage:</span> full control including policies and
                    grants.
                  </li>
                </ul>
              </div>
            }
          />
        </div>
        {canEdit && (
          <UserAutocomplete
            handleSelectUser={(user) =>
              handleAddUser({ email: user.email, full_name: user.full_name })
            }
            selectedUsers={Array.from(existingUserPrincipals).map((p) => ({
              email: principalTypeAndId(p).id,
            }))}
            autoRefocus
          />
        )}
        <div className="space-y-2">
          {visibleUserGrants.map((grant) => {
            const { id } = principalTypeAndId(grant.principal);
            const effectivePerm = getEffectivePermission(grant, pendingUpdates);
            const displayName = getUserNameByEmail(id, users);
            const imageUrl = findUserByEmail(users, id)?.image_url;
            const isRemoving = removedKeys.has(`${grant.principal}:${grant.permission}`);
            return (
              <GrantRow
                key={`${grant.principal}:${grant.permission}`}
                label={displayName}
                subText={displayName !== id ? id : undefined}
                permission={effectivePerm}
                canEdit={canEdit}
                isRemoving={isRemoving}
                icon={<PersonAvatar name={displayName} email={id} imageUrl={imageUrl} />}
                onPermissionChange={(newPerm) => {
                  if (newPerm === "remove") {
                    onRemoveAccess({
                      principal: grant.principal,
                      permission: grant.permission,
                      label: displayName,
                    });
                  } else {
                    onUpdateAccess({
                      principal: grant.principal,
                      oldPermission: grant.permission,
                      newPermission: newPerm,
                      label: displayName,
                    });
                  }
                }}
              />
            );
          })}
          {pendingUserAdditions.map((addition) => (
            <GrantRow
              key={`pending-user-${addition.identifier}`}
              label={addition.label}
              subText={addition.label !== addition.identifier ? addition.identifier : undefined}
              permission={addition.permission}
              canEdit={canEdit}
              icon={
                <PersonAvatar
                  name={addition.label}
                  email={addition.identifier}
                  imageUrl={findUserByEmail(users, addition.identifier)?.image_url}
                />
              }
              onPermissionChange={(newPerm) => {
                if (newPerm === "remove") {
                  onRemoveAccess({
                    principal: userPrincipal(addition.identifier),
                    permission: addition.permission,
                    label: addition.label,
                  });
                } else {
                  onUpdateAccess({
                    principal: userPrincipal(addition.identifier),
                    oldPermission: addition.permission,
                    newPermission: newPerm,
                    label: addition.label,
                  });
                }
              }}
            />
          ))}
          {totalUsers === 0 && (
            <p className="text-sm text-muted-foreground py-2">No users have been granted access.</p>
          )}
        </div>
      </div>

      <div className="pt-2 space-y-3">
        <div className="flex items-center gap-1.5">
          <Label>Groups ({totalGroups})</Label>
          <HelpTooltip
            content={`Groups with access to this ${resourceNoun}. Access applies to all current and future members of the group.`}
          />
        </div>
        {canEdit && (
          <Select
            value={selectedGroup}
            onValueChange={handleAddGroup}
            disabled={availableGroups.length === 0}
          >
            <SelectTrigger className="w-full bg-background border-border">
              <SelectValue
                placeholder={availableGroups.length === 0 ? "No groups available" : "Select group"}
              />
            </SelectTrigger>
            <SelectContent>
              {availableGroups.map((g) => (
                <SelectItem key={g.name} value={g.name}>
                  {g.display_name || g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="space-y-2">
          {visibleGroupGrants.map((grant) => {
            const { id } = principalTypeAndId(grant.principal);
            const effectivePerm = getEffectivePermission(grant, pendingUpdates);
            const group = groups.find((g) => g.name === id);
            const displayName = group?.display_name || id;
            const isRemoving = removedKeys.has(`${grant.principal}:${grant.permission}`);
            return (
              <GrantRow
                key={`${grant.principal}:${grant.permission}`}
                label={displayName}
                permission={effectivePerm}
                canEdit={canEdit}
                isRemoving={isRemoving}
                icon={
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    <Users className="h-4 w-4" />
                  </div>
                }
                onPermissionChange={(newPerm) => {
                  if (newPerm === "remove") {
                    onRemoveAccess({
                      principal: grant.principal,
                      permission: grant.permission,
                      label: displayName,
                    });
                  } else {
                    onUpdateAccess({
                      principal: grant.principal,
                      oldPermission: grant.permission,
                      newPermission: newPerm,
                      label: displayName,
                    });
                  }
                }}
              />
            );
          })}
          {pendingGroupAdditions.map((addition) => (
            <GrantRow
              key={`pending-group-${addition.identifier}`}
              label={addition.label}
              permission={addition.permission}
              canEdit={canEdit}
              icon={
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                  <Users className="h-4 w-4" />
                </div>
              }
              onPermissionChange={(newPerm) => {
                if (newPerm === "remove") {
                  onRemoveAccess({
                    principal: `user-tag:${addition.identifier}`,
                    permission: addition.permission,
                    label: addition.label,
                  });
                } else {
                  onUpdateAccess({
                    principal: `user-tag:${addition.identifier}`,
                    oldPermission: addition.permission,
                    newPermission: newPerm,
                    label: addition.label,
                  });
                }
              }}
            />
          ))}
          {totalGroups === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              No groups have been granted access.
            </p>
          )}
        </div>
      </div>

      <InferredGrantsSection
        inferredGrantsUrl={`${grantsUrl}?include_inferred=true`}
        groups={groups}
      />
    </div>
  );
};

interface InferredGrantsSectionProps {
  inferredGrantsUrl: string;
  groups: { name: string; display_name?: string }[];
}

const InferredGrantsSection = ({ inferredGrantsUrl, groups }: InferredGrantsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading } = useFetcher<GrantListResponse>(isOpen ? inferredGrantsUrl : null);

  const items = (data?.items ?? []).filter((g) => g.source === "inferred");
  const count = data !== undefined ? items.length : null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="pt-2">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex items-center gap-1.5 w-full text-left">
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isOpen && "rotate-90"
            )}
          />
          <Label className="cursor-pointer">
            Inferred grants{count !== null ? ` (${count})` : ""}
          </Label>
          <HelpTooltip content="Groups with access through inferred FGA grants (e.g. via admin group membership). These are read-only — revoke the underlying group membership to remove access." />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="h-10 bg-muted animate-pulse rounded" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No inferred grants.</p>
        ) : (
          <div className="space-y-2">
            {items.map((grant) => {
              const { id } = principalTypeAndId(grant.principal);
              const group = groups.find((g) => g.name === id);
              const displayName = group?.display_name || id;
              return (
                <InferredGrantRow
                  key={grant.principal}
                  label={displayName}
                  permission={grant.permission}
                />
              );
            })}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

const InferredGrantRow = ({
  label,
  permission,
}: {
  label: string;
  permission: GrantPermission;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
        <Users className="h-4 w-4" />
      </div>
      <span className="font-medium text-sm text-foreground truncate">{label}</span>
    </div>
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <Lock className="h-3.5 w-3.5" />
      <span className="text-sm">{PERMISSION_LABELS[permission]}</span>
    </div>
  </div>
);

const GrantRow = ({
  label,
  subText,
  permission,
  canEdit,
  isRemoving = false,
  icon,
  onPermissionChange,
}: {
  label: string;
  subText?: string;
  permission: GrantPermission;
  canEdit: boolean;
  isRemoving?: boolean;
  icon: React.ReactNode;
  onPermissionChange: (value: GrantPermission | "remove") => void;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {icon}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="font-medium text-sm text-foreground truncate">{label}</span>
        {subText && <span className="text-sm text-muted-foreground truncate">{subText}</span>}
      </div>
    </div>
    {canEdit ? (
      <Select
        value={isRemoving ? "remove" : permission}
        onValueChange={(value) => onPermissionChange(value as GrantPermission | "remove")}
      >
        <SelectTrigger className="w-[110px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERMISSION_ORDER.map((p) => (
            <SelectItem key={p} value={p}>
              {PERMISSION_LABELS[p]}
            </SelectItem>
          ))}
          <SelectItem value="remove" className="text-destructive focus:text-destructive">
            Remove
          </SelectItem>
        </SelectContent>
      </Select>
    ) : (
      <span className="text-sm text-muted-foreground">{PERMISSION_LABELS[permission]}</span>
    )}
  </div>
);

export default AdminGrantsPanel;
